import catchupLogoHeader from '../assets/landing/catchup-logo-header.png';

const colors = [
  ['Ink navy', '#071039', 'Primary text, hero headings, and nav labels.'],
  ['Action violet', '#3926E8', 'Primary calls to action and active states.'],
  ['Close coral', '#F36D64', 'Relationship emphasis, highlights, and warm accents.'],
  ['Soft lavender', '#F1EDFF', 'Pills, benefit icons, and quiet supporting surfaces.'],
  ['Cloud surface', '#FBFCFF', 'Page background and breathing room.'],
  ['Slate copy', '#404B75', 'Long-form copy and secondary guidance.'],
] as const;

const buttonRecipes = [
  [
    'Primary call to action',
    'bg-[#3926E8] text-white shadow-[0_18px_35px_rgba(57,38,232,0.22)]',
    'Use a minimum 64px hero target, clear focus rings, and direct action copy.',
  ],
  [
    'Secondary media action',
    'border border-[#C9CFDF] bg-white text-[#071039] shadow-sm',
    'Pair with a familiar media icon, visible border, and the same generous hit area.',
  ],
] as const;

const cardRecipes = [
  ['Benefit tile', 'Use compact icon-first tiles for quick proof points.', 'bg-white'],
  ['Problem card', 'Use soft radial surfaces for narrative examples and live DOM artwork.', 'bg-[#FAF9FF]'],
  ['Trust marker', 'Use simple icon plus sentence rows for credibility cues.', 'bg-[#FDFBF7]'],
] as const;

const sectionPatterns = [
  ['landing-header', 'Logo image, desktop nav, login, primary CTA, and mobile menu affordance.'],
  ['hero-product-split', 'Eyebrow pill, serif promise, CTAs, benefit tray, and real product media.'],
  ['section-title-stack', 'Eyebrow pill, serif section title, optional coral emphasis, and lead copy.'],
  ['app-preview-card-grid', 'Two to three cards with live product UI fragments and local state feedback.'],
  ['trust-compliance-band', 'Quiet legal/SMS proof that stays subordinate to the product story.'],
] as const;

const heroRules = [
  'Use picture sources for desktop and mobile crops',
  'Keep the app preview inspectable',
  'Favor real product state over abstract atmosphere',
  'Let a hint of the next section remain visible',
] as const;

const spacingTokens = [
  ['gutterMobile', '24px', 'Mobile page gutter.'],
  ['stackMd', '28px', 'Hero pill to heading and copy rhythm.'],
  ['sectionY', '64px', 'Default vertical section padding.'],
  ['cardPad', '20px', 'Standard app-preview card padding.'],
] as const;

const shadowTokens = [
  ['cta', '0 18px 35px rgba(57, 38, 232, 0.22)', 'Purple-tinted, not black.'],
  ['headerCta', '0 12px 28px rgba(57, 38, 232, 0.25)', 'Smaller than hero CTA.'],
  ['tray', '0 24px 70px rgba(17, 24, 68, 0.10)', 'Deep but soft ink shadow.'],
  ['card', '0 24px 64px rgba(44, 32, 112, 0.10)', 'Default large card shadow.'],
  ['cardHover', '0 30px 78px rgba(44, 32, 112, 0.12)', 'Use with small upward transform.'],
] as const;

const iconLanguage = [
  'Use a compact icon mark in benefit trays.',
  'Prefer approved small icons; avoid decorative illustrations inside trays.',
  'Use consistent line icons with CatchUp colors when no approved asset exists.',
  'Icon wells use primarySoft (#F1EDFF) with primary (#3926E8) marks.',
] as const;

const forbiddenPatterns = [
  'No blurred stock-style hero art',
  'No purple-only palette drift',
  'No nested cards',
  'No oversized marketing cards for operational details',
  'No text that explains how to use the page inside the UI',
] as const;

const sourceStatus = [
  ['Primary visual source', 'owned-authorized', 'CompliancePages.tsx header and hero'],
  ['Brand mark', 'owned-authorized', 'catchup-logo-header.png'],
  ['Token source', 'owned-authorized', 'docs/web-landing-art-bible.tokens.json'],
] as const;

function Section({
  children,
  eyebrow,
  id,
  title,
}: {
  children: React.ReactNode;
  eyebrow: string;
  id: string;
  title: string;
}) {
  return (
    <section
      aria-labelledby={id}
      className="border-t border-[#DDE2EE] bg-white px-6 py-12 md:px-8"
    >
      <div className="mx-auto max-w-[1180px]">
        <p className="text-sm font-black uppercase tracking-normal text-[#3926E8]">{eyebrow}</p>
        <h2
          className="mt-3 font-serif text-[34px] font-black leading-[1.05] tracking-normal text-[#071039] md:text-[46px]"
          id={id}
        >
          {title}
        </h2>
        <div className="mt-7">{children}</div>
      </div>
    </section>
  );
}

export default function LandingStyleguidePage() {
  return (
    <div className="min-h-screen overflow-x-hidden bg-[#FBFCFF] text-[#071039]">
      <header className="relative z-20 bg-white/90">
        <div className="mx-auto flex max-w-[1480px] flex-wrap items-center justify-between gap-5 px-6 py-6 md:flex-nowrap md:px-8">
          <a className="block shrink-0" href="/">
            <img
              alt="CatchUp"
              className="h-[45px] w-[185px] object-contain md:h-[54px] md:w-[222px]"
              src={catchupLogoHeader}
            />
          </a>
          <a
            className="inline-flex min-h-11 items-center justify-center rounded-xl bg-[#3926E8] px-6 text-base font-bold text-white shadow-[0_12px_28px_rgba(57,38,232,0.25)] hover:bg-[#2F20C8] focus:outline-none focus:ring-4 focus:ring-[#3926E8]/30"
            href="/"
          >
            Public homepage
          </a>
        </div>
      </header>

      <main>
        <section className="mx-auto grid max-w-[1480px] gap-9 px-6 pb-12 pt-8 md:px-8 lg:grid-cols-[0.52fr_0.48fr] lg:items-center">
          <div className="max-w-[720px]">
            <p className="mb-7 inline-flex items-center gap-3 rounded-full bg-[#F1EDFF] px-4 py-3 text-base font-medium text-[#3926E8]">
              <span aria-hidden="true" className="text-2xl text-[#F36D64]">
                *
              </span>
              Source status
            </p>
            <h1 className="max-w-[760px] font-serif text-[40px] font-black leading-[1.04] tracking-normal text-[#071039] sm:text-[64px] lg:text-[72px]">
              CatchUp web landing styleguide
            </h1>
            <p className="mt-8 max-w-[620px] text-[22px] leading-[1.45] text-[#071039]">
              Derived from the current public homepage and reserved for local
              preview work on the CatchUp landing design system.
            </p>
          </div>

          <div className="relative overflow-hidden rounded-[20px] bg-white p-5 shadow-[0_24px_70px_rgba(17,24,68,0.10)]">
            <div className="rounded-[16px] bg-[#F1EDFF] p-5">
              <p className="text-sm font-black uppercase tracking-normal text-[#3926E8]">Hero media</p>
              <div className="mt-5 overflow-hidden rounded-[20px] bg-[#FBFCFF] shadow-[inset_0_0_0_1px_rgba(57,38,232,0.10)]">
                <div className="grid min-h-[310px] place-items-center bg-[radial-gradient(circle_at_28%_24%,rgba(243,109,100,0.20),transparent_24%),radial-gradient(circle_at_78%_34%,rgba(57,38,232,0.18),transparent_28%),linear-gradient(135deg,#FFFFFF_0%,#FBFCFF_100%)] p-6">
                  <div className="w-full max-w-[340px] rounded-[28px] border-[7px] border-[#E5E5EC] bg-white p-5 shadow-[0_18px_42px_rgba(45,42,90,0.12)]">
                    <div className="mx-auto mb-5 h-2 w-20 rounded-full bg-[#E5E5EC]" />
                    <p className="text-sm font-bold text-[#3926E8]">Saturday plan</p>
                    <h2 className="mt-3 text-2xl font-black leading-tight text-[#071039]">
                      Dinner with Maya and Noah
                    </h2>
                    <div className="mt-5 grid gap-3">
                      <span className="rounded-[13px] bg-[#F1EDFF] px-4 py-3 text-base font-semibold text-[#071039]">
                        6:30 PM works for everyone
                      </span>
                      <span className="rounded-[13px] bg-[#FFF1ED] px-4 py-3 text-base font-semibold text-[#071039]">
                        Send one gentle reminder
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <Section eyebrow="Palette" id="color-swatches-title" title="Color swatches">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {colors.map(([name, hex, usage]) => (
              <article className="rounded-[20px] border border-[#DDE2EE] bg-white p-5 shadow-sm" key={name}>
                <div
                  aria-hidden="true"
                  className="h-20 rounded-[16px] border border-black/5"
                  style={{ backgroundColor: hex }}
                />
                <h3 className="mt-4 text-xl font-black text-[#071039]">{name}</h3>
                <p className="mt-1 font-mono text-sm font-bold text-[#404B75]">{hex}</p>
                <p className="mt-3 text-base leading-7 text-[#404B75]">{usage}</p>
              </article>
            ))}
          </div>
        </Section>

        <Section eyebrow="Type" id="typography-samples-title" title="Typography samples">
          <div className="grid gap-5 lg:grid-cols-[1.1fr_0.9fr]">
            <div className="rounded-[20px] border border-[#DDE2EE] bg-white p-6">
              <p className="text-sm font-black uppercase tracking-normal text-[#3926E8]">Hero serif display</p>
              <p className="mt-4 font-serif text-[38px] font-black leading-[1.04] tracking-normal text-[#071039] md:text-[56px]">
                Stay close without the scheduling back-and-forth.
              </p>
            </div>
            <div className="grid gap-5">
              <article className="rounded-[20px] border border-[#DDE2EE] bg-[#FBFCFF] p-6">
                <h3 className="text-3xl font-black leading-tight text-[#071039]">Section heading</h3>
                <p className="mt-3 text-lg leading-8 text-[#404B75]">
                  Use sturdy, compact headings for scannable sections.
                </p>
              </article>
              <article className="rounded-[20px] border border-[#DDE2EE] bg-[#FDFBF7] p-6">
                <h3 className="text-xl font-black text-[#071039]">Body copy</h3>
                <p className="mt-3 text-[17px] leading-7 text-[#404B75]">
                  Keep body text warm, direct, and grounded in real scheduling
                  outcomes.
                </p>
              </article>
            </div>
          </div>
        </Section>

        <Section eyebrow="Controls" id="button-recipes-title" title="Button recipes">
          <div className="grid gap-5 md:grid-cols-2">
            {buttonRecipes.map(([label, classes, guidance]) => (
              <article className="rounded-[20px] border border-[#DDE2EE] bg-white p-6" key={label}>
                <button
                  className={`inline-flex min-h-16 items-center justify-center rounded-xl px-8 text-lg font-bold transition hover:-translate-y-0.5 focus:outline-none focus:ring-4 focus:ring-[#3926E8]/30 ${classes}`}
                  type="button"
                >
                  {label}
                </button>
                <p className="mt-5 text-base leading-7 text-[#404B75]">
                  {guidance}
                </p>
              </article>
            ))}
          </div>
        </Section>

        <Section eyebrow="Surfaces" id="card-recipes-title" title="Card recipes">
          <div className="grid gap-5 md:grid-cols-3">
            {cardRecipes.map(([title, body, surface]) => (
              <article
                className={`min-h-[220px] rounded-[20px] border border-[#DDE2EE] p-6 shadow-[0_22px_55px_rgba(17,24,68,0.08)] ${surface}`}
                key={title}
              >
                <span
                  aria-hidden="true"
                  className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-[#F1EDFF] text-2xl font-black text-[#3926E8]"
                >
                  +
                </span>
                <h3 className="mt-5 text-2xl font-black leading-tight text-[#071039]">{title}</h3>
                <p className="mt-3 text-base leading-7 text-[#404B75]">{body}</p>
              </article>
            ))}
          </div>
        </Section>

        <Section eyebrow="Patterns" id="section-patterns-title" title="Section patterns">
          <div className="grid gap-4 lg:grid-cols-2">
            {sectionPatterns.map(([name, guidance]) => (
              <article className="rounded-[20px] border border-[#DDE2EE] bg-white p-5 shadow-sm" key={name}>
                <h3 className="font-mono text-base font-black text-[#3926E8]">{name}</h3>
                <p className="mt-3 text-base leading-7 text-[#404B75]">{guidance}</p>
              </article>
            ))}
          </div>
        </Section>

        <Section eyebrow="Media" id="hero-image-rules-title" title="Hero image rules">
          <ul className="grid gap-4 md:grid-cols-2">
            {heroRules.map((rule) => (
              <li
                className="flex min-h-20 items-center gap-4 rounded-[20px] border border-[#DDE2EE] bg-white p-5 text-lg font-bold text-[#071039]"
                key={rule}
              >
                <span
                  aria-hidden="true"
                  className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-[#FFF1ED] text-[#F36D64]"
                >
                  ✓
                </span>
                {rule}
              </li>
            ))}
          </ul>
        </Section>

        <Section eyebrow="Scale" id="spacing-scale-title" title="Spacing scale">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {spacingTokens.map(([name, value, role]) => (
              <article className="rounded-[20px] border border-[#DDE2EE] bg-[#FBFCFF] p-5" key={name}>
                <h3 className="font-mono text-base font-black text-[#3926E8]">{name}</h3>
                <p className="mt-3 text-3xl font-black text-[#071039]">{value}</p>
                <p className="mt-3 text-base leading-7 text-[#404B75]">{role}</p>
              </article>
            ))}
          </div>
        </Section>

        <Section eyebrow="Depth" id="shadow-tokens-title" title="Shadow tokens">
          <div className="grid gap-4 md:grid-cols-2">
            {shadowTokens.map(([name, value, note]) => (
              <article className="rounded-[20px] border border-[#DDE2EE] bg-white p-5 shadow-sm" key={name}>
                <h3 className="font-mono text-base font-black text-[#3926E8]">{name}</h3>
                <p className="mt-3 font-mono text-sm font-bold leading-6 text-[#071039]">{value}</p>
                <p className="mt-3 text-base leading-7 text-[#404B75]">{note}</p>
              </article>
            ))}
          </div>
        </Section>

        <Section eyebrow="Marks" id="icon-language-title" title="Icon language">
          <ul className="grid gap-4 md:grid-cols-2">
            {iconLanguage.map((guidance) => (
              <li
                className="flex min-h-20 items-center gap-4 rounded-[20px] border border-[#DDE2EE] bg-white p-5 text-lg font-bold text-[#071039]"
                key={guidance}
              >
                <span
                  aria-hidden="true"
                  className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-[#F1EDFF] text-xl font-black text-[#3926E8]"
                >
                  ◎
                </span>
                {guidance}
              </li>
            ))}
          </ul>
        </Section>

        <Section eyebrow="Guardrails" id="forbidden-patterns-title" title="Forbidden patterns">
          <ul className="grid gap-4 md:grid-cols-2">
            {forbiddenPatterns.map((pattern) => (
              <li
                className="flex min-h-20 items-center gap-4 rounded-[20px] border border-[#F3C8C3] bg-[#FFF8F7] p-5 text-lg font-bold text-[#071039]"
                key={pattern}
              >
                <span
                  aria-hidden="true"
                  className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-white text-[#F36D64]"
                >
                  !
                </span>
                {pattern}
              </li>
            ))}
          </ul>
        </Section>

        <Section eyebrow="Status" id="source-status-title" title="Source status">
          <p className="max-w-[720px] text-lg leading-8 text-[#404B75]">
            Derived from the current public homepage and reserved for local
            preview work on the CatchUp landing design system.
          </p>
          <div className="mt-6 grid gap-4 md:grid-cols-3">
            {sourceStatus.map(([role, status, source]) => (
              <article className="rounded-[20px] border border-[#DDE2EE] bg-[#FBFCFF] p-5" key={source}>
                <h3 className="text-lg font-black text-[#071039]">{role}</h3>
                <p className="mt-3 font-mono text-sm font-black text-[#3926E8]">{status}</p>
                <p className="mt-3 text-base leading-7 text-[#404B75]">{source}</p>
              </article>
            ))}
          </div>
        </Section>
      </main>
    </div>
  );
}
